import axios from 'axios';
import {Slide, Link} from './types'

const API_URL = 'https://api.productscript.ru';

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
    baseURL: API_URL,
});

interface UserProfile {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    image_url: string;
}
interface Permissions {
    can_read_projects: boolean;
    can_edit_projects: boolean;
    can_read_products: boolean;
    can_edit_products: boolean;
}

// Добавляем интерсептор для добавления токена в заголовки запросов
api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response.status === 401 && !originalRequest._retry) {
            console.log(error.response.data.detail)
            if (error.response.data.detail === "Invalid refresh token"){
                logoutUser();
            }
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            return new Promise((resolve, reject) => {
                const refresh_token = localStorage.getItem('refresh_token');
                api.post('/oauth/refresh', { refresh_token })
                    .then(response => {
                        const newToken = response.data.access_token;
                        localStorage.setItem('access_token', newToken);
                        localStorage.setItem('refresh_token', response.data.refresh_token);
                        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                        processQueue(null, newToken);
                        resolve(api(originalRequest));
                    })
                    .catch(err => {
                        processQueue(err, null);
                        logoutUser();
                        reject(err);
                    })
                    .finally(() => {
                        isRefreshing = false;
                    });
            });
        }
        return Promise.reject(error);
    }
);

const logoutUser = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
};

// Функция для авторизации и получения токена
export const login = async (username: string, password: string) => {
    try {
        const response = await api.post('/token', {username, password});
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user_id', response.data.user_id);
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error; // Проброс ошибки для обработки в компоненте
    }
};

// Функция для авторизации и получения токена
export const forgot = async (email: string) => {
    try {
        const response = await api.post('/password_reset/request', {email});
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error; // Проброс ошибки для обработки в компоненте
    }
};

// Функция для регистрации
export const register = async (email: string, password: string) => {
    try {
        const response = await api.post('/users/', {email, password});
        return response.data;
    } catch (error) {
        console.error('Registration error:', error);
        throw error;
    }
};

// Функция для подтверждения email
export const confirmEmailPath = async (token: string) => {
    try {
        const response = await api.get('/users/confirm-email', {
            params: {token},
        });
        return response.data;
    } catch (error) {
        console.error('Email confirmation error:', error);
        throw error;
    }
};

export const resetPassword = async (email: string, otp: string, newPassword: string) => {
    const response = await api.post('/password_reset/confirm', { email, otp, new_password: newPassword });
    return response.data;
};

export const getUserPermissions = async (userId: string) => {
    const response = await api.get(`/users/get_permissions/${userId}`);
    return response.data;
};

// Функция для проверки аутентификации
export const isAuthenticated = () => {
    return !!localStorage.getItem('access_token');
};

// Функция для выхода из системы
export const logout = () => {
    localStorage.removeItem('access_token');
};

export const fetchProjects = async (page: number, limit: number) => {
    try {
        const response = await api.get('/projects/with-first-slide/', { params: { page, limit } });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('Error fetching projects:', error.response?.data || error.message);
        } else {
            console.error('Unexpected error:', error);
        }
        throw error; // Rethrow the error after logging
    }
};
export const fetchProject = async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}`);
    return response.data;
};


export const fetchProjectChanges = async (projectId: string | null, page: number) => {
    try {
        const response = await api.get(`/projects/${projectId}/changes`, {
            params: { page },
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching project changes:', error);
        throw error;
    }
};

export const deleteProject = async (id: string) => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
};

export const getProject = async (id: string) => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
};

export const uploadProjectImage = async (projectId: string, file: File, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/projects/${projectId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};


export const getUserProfile = async () => {
    const response = await api.get('/users/me/');
    return response.data;
};

export const updateProfile = async (profile: UserProfile) => {
    const response = await api.put(`/users/${profile.id}`, profile);
    return response.data;
};

export const changePassword = async (old_password: string, new_password: string) => {
    const response = await api.post('/users/change_password', { old_password, new_password });
    return response.data;
};

export const uploadUserAvatar = async (userId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/users/${userId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading avatar:', error);
        throw error;
    }
};

export const fetchCompanyMembers = async (page: number, limit: number) => {
    const response = await api.get('/users/company-users/', { params: { page, limit } });
    return response.data;
};

export const addCompanyMember = async (email: string) => {
    const response = await api.post(`/users/add_to_team?email=${ email }`, {  });
    return response.data;
};

export const updateCompanyMember = async (userId: string, data: { is_active: boolean }) => {
    const response = await api.put(`/users/${userId}`, data);
    return response.data;
};

export const deleteCompanyMember = async (userId: string) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
};

export const fetchUserPermissions = async (userId: string): Promise<Permissions> => {
    try {
        const response = await api.get(`/users/get_permissions/${userId}`);
        // console.log(response.data)
        return response.data;
    } catch (error) {
        console.error('Error fetching user permissions:', error);
        throw error;
    }
};

export const setUserPermissions = async (userId: string, permissions: Permissions) => {
    const response = await api.put(`/users/update_permissions/${userId}`, {...permissions });
    return response.data;
};


export const createVideoFolder = async (folderName: string) => {
    try {
        const response = await api.post('/videos/folders/', { name: folderName });
        return response.data;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
};

export const getVideoFolders = async () => {
    try {
        const response = await api.get('/videos/folders/');
        return response.data;
    } catch (error) {
        console.error('Error fetching folders:', error);
        throw error;
    }
};

export const getVideos = async (params: { page?: number; limit?: number; folder_id?: string | null; sortBy?: string }) => {
    try {
        const response = await api.get('/videos/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching videos:', error);
        throw error;
    }
};

export const uploadVideo = async (file: File, folderId: string | null, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
        formData.append('folder_id', folderId);
    }
    try {
        const response = await api.post('/videos/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading video:', error);
        throw error;
    }
};

export const uploadVideoWEBM = async (file: File, folderId: string | null, slideId: string | null, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
        formData.append('folder_id', folderId);
    }
    if (slideId) {
        formData.append('slide_id', slideId);
    }
    try {
        const response = await api.post('/videos/uploadwebm', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading video:', error);
        throw error;
    }
};

export const deleteVideoFolder = async (folderId: string) => {
    try {
        const response = await api.delete(`/videos/folders/${folderId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
};

export const deleteVideo = async (videoId: string) => {
    try {
        const response = await api.delete(`/videos/${videoId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting video:', error);
        throw error;
    }
};



export const createAudioFolder = async (folderName: string) => {
    try {
        const response = await api.post('/audios/folders/', { name: folderName });
        return response.data;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
};

export const getAudioFolders = async () => {
    try {
        const response = await api.get('/audios/folders/');
        return response.data;
    } catch (error) {
        console.error('Error fetching folders:', error);
        throw error;
    }
};

export const getAudios = async (params: { page?: number; limit?: number; folder_id?: string | null; slide_id?: string | null; sortBy?: string }) => {
    try {
        const response = await api.get('/audios/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching audios:', error);
        throw error;
    }
};

export const uploadAudio = async (file: File, folderId: string | null, slideId: string | null, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('time', '0');
    if (folderId) {
        formData.append('folder_id', folderId);
    }
    if (slideId) {
        formData.append('slide_id', slideId);
    }
    try {
        const response = await api.post('/audios/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading audio:', error);
        throw error;
    }
};

export const deleteAudioFolder = async (folderId: string) => {
    try {
        const response = await api.delete(`/audios/folders/${folderId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
};

export const deleteAudio = async (audioId: string) => {
    try {
        const response = await api.delete(`/audios/${audioId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting audio:', error);
        throw error;
    }
};
export const updateAudioTime = async (id: string, time: number) => {
    try {
        const response = await api.put(`/audios/${id}`,{ time });
        return response.data;
    } catch (error) {
        console.error('Error deleting audio:', error);
        throw error;
    }
};



export const createPDFFolder = async (folderName: string) => {
    try {
        const response = await api.post('/pdfs/folders/', { name: folderName });
        return response.data;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
};

export const getPDFFolders = async () => {
    try {
        const response = await api.get('/pdfs/folders/');
        return response.data;
    } catch (error) {
        console.error('Error fetching folders:', error);
        throw error;
    }
};

export const getPDFs = async (params: { page?: number; limit?: number; folder_id?: string | null; sortBy?: string }) => {
    try {
        const response = await api.get('/pdfs/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching pdfs:', error);
        throw error;
    }
};

export const uploadPDF = async (file: File, folderId: string | null, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
        formData.append('folder_id', folderId);
    }
    try {
        const response = await api.post('/pdfs/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                }
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading pdf:', error);
        throw error;
    }
};

export const deletePDFFolder = async (folderId: string) => {
    try {
        const response = await api.delete(`/pdfs/folders/${folderId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
};

export const deletePDF = async (pdfId: string) => {
    try {
        const response = await api.delete(`/pdfs/${pdfId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting pdf:', error);
        throw error;
    }
};


export const fetchProducts = async (page: number, limit: number = 100) => {
    try {
        const response = await api.get('/products/', {
            params: { page, limit }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
};



export const uploadPresentation = async (projectId: string, file: File, onProgress: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/projects/${projectId}/upload-presentation`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        },
    });
    return response.data;
};

export const getSlides = async (projectId: string) => {
    const response = await api.get('/slides/', { params: { project_id: projectId, page: 1, limit: 1000 } });
    return response.data;
};

export const getChapters = async (projectId: string) => {
    const response = await api.get('/chapters/', { params: { project_id: projectId } });
    return response.data;
};

export const addChapter = async (data: { name: string; project_id: string; sort: number; external_id?: string }) => {
    const response = await api.post('/chapters/', data);
    return response.data;
};

export const getChapterSlides = async (chapterId: string) => {
    const response = await api.get(`/chapter-slides/${chapterId}`);
    return response.data;
};

export const getSlide = async (slideId: string) => {
    const response = await api.get(`/slides/${slideId}`);
    return response.data;
};

export const addSlideToChapter = async (chapterId: string, slideId: string) => {
    try {
        const response = await api.post(`/chapter-slides/${chapterId}`, [slideId]);
        return response.data;
    } catch (error) {
        console.error('Error adding slide to chapter:', error);
        throw error;
    }
};
export const addSlidesToChapter = async (chapterId: string, slideIds: string[]) => {
    try {
        const response = await api.post(`/chapter-slides/${chapterId}`, slideIds);
        return response.data;
    } catch (error) {
        console.error('Error adding slide to chapter:', error);
        throw error;
    }
};

export const reorderChapterSlides = async (chapterId: string, newOrder: string[]) => {
    try {
        const response = await api.put(`/chapter-slides/${chapterId}/reorder`, newOrder);
        return response.data;
    } catch (error) {
        console.error('Error reordering slides:', error);
        throw error;
    }
};

export const deleteChapterSlide = async (chapterSlideId: string) => {
    try {
        const response = await api.delete(`/chapter-slides/${chapterSlideId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting chapter slide:', error);
        throw error;
    }
};

export const updateSlide = async (slideId: string, data: Partial<Slide>) => {
    const response = await api.put(`/slides/${slideId}`, data);
    return response.data;
};
export const refreshSlide = async (slideId: string) => {
    const response = await api.put(`/slides/${slideId}/refresh_html`);
    return response.data;
};
export const updateSlideHtml = async (slideId: string, data: Partial<Slide>) => {
    const response = await api.put(`/slides/${slideId}/html`, data);
    return response.data;
};
export const deleteSlide = async (slideId: string) => {
    const response = await api.delete(`/slides/${slideId}`);
    return response.data;
};

export const createLink = async (linkData: {
    name: string;
    link_type: string;
    url: string;
    slide_id: string | undefined;
}) => {
    const response = await api.post('/links/', linkData);
    return response.data;
};
export const getLink = async (linkId: string) => {
    const response = await api.get(`/links/${linkId}`);
    return response.data;
};
export const getLinkSlides = async (linkId: string) => {
    const response = await api.get(`/link-slides/${linkId}`);
    return response.data;
};
export const updateLink = async (linkId: string, data: Partial<Link>) => {
    const response = await api.put(`/links/${linkId}`, data);
    return response.data;
};
export const addSlideToLink = async (linkId: string, slideIds: string[]) => {
    try {
        const response = await api.post(`/link-slides/${linkId}`, slideIds);
        return response.data;
    } catch (error) {
        console.error('Error adding slide to chapter:', error);
        throw error;
    }
};
export const deleteLinkSlide = async (linkId: string, linkSlideId: string) => {
    try {
        const response = await api.delete(`/link-slides/${linkId}/${linkSlideId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting chapter slide:', error);
        throw error;
    }
};

export const processProject = async (project_id: string) => {
    const response = await api.post(`/projects/${project_id}/process`, {});

    if (response.status === 500) {
        throw new Error('Server error 500');
    }

    return response.data;
};

export const processZipProject = async (project_id: string) => {
    const response = await api.post(`/projects/${project_id}/create_archive_dev`, {});
    return response.data;
};

export const getScenarios = async (page?: number, limit?: number) => {
    try {
        const response = await api.get('/scenarios/', {
            params: { page, limit }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching audios:', error);
        throw error;
    }
};
export const addScaenario = async (data: { name: string; brand_id: string }) => {
    const response = await api.post('/scenarios/', data);
    return response.data;
};

export const addVisit = async (data: { name: string; scenario_id: string; sort: number; external_id?: string }) => {
    const response = await api.post('/visits/', data);
    return response.data;
};
export const addSlideToVisit = async (visitId: string, slideId: string) => {
    try {
        const response = await api.post(`/visit-slides/${visitId}`, [slideId]);
        return response.data;
    } catch (error) {
        console.error('Error adding slide to visit:', error);
        throw error;
    }
};
export const getVisits = async (scenarioId: string, page: number, limit: number) => {
    const response = await api.get('/visits/', { params: { scenario_id: scenarioId, page: page, limit: limit } });
    return response.data;
};
export const getSegmentVisits = async () => {
    const response = await api.get('/visits/segment_visits', { params: {  } });
    return response.data;
};
export const getVisitSlides = async (visit_id: string) => {
    const response = await api.get('/visit-slides/', { params: { visit_id: visit_id } });
    return response.data;
};
export const deleteVisit = async (visitId: string) => {
    try {
        const response = await api.delete(`/visits/${visitId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting visit slide:', error);
        throw error;
    }
};
export const reorderVisitSlides = async (visitId: string, newOrder: string[]) => {
    try {
        const response = await api.put(`/visit-slides/${visitId}/reorder`, newOrder);
        return response.data;
    } catch (error) {
        console.error('Error reordering slides:', error);
        throw error;
    }
};

export const deleteVisitSlide = async (visitSlideId: string) => {
    try {
        const response = await api.delete(`/visit-slides/${visitSlideId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting visit slide:', error);
        throw error;
    }
};
export const fetchScenario = async (scenario_id: string) => {
    const response = await api.get(`/scenarios/${scenario_id}`);
    return response.data;
};

export const addSlideToHot = async (data: { slide_id: string; parent_id: string }) => {
    const response = await api.post('/hot-slides/', data);
    return response.data;
};
export const duplicateHotSlide = async (project_id: string, slide_id: string) => {
    const response = await api.post(`/hot-slides/apply-to-all/${project_id}/${slide_id}`, {});
    return response.data;
};
export const getHotSlides = async (parent_id: string) => {
    const response = await api.get(`/hot-slides/parent/${parent_id}`, { params: {  } });
    return response.data;
};
export const reorderHotSlides = async (parent_id: string, newOrder: string[]) => {
    try {
        const response = await api.put(`/hot-slides/${parent_id}/reorder`, newOrder);
        return response.data;
    } catch (error) {
        console.error('Error reordering slides:', error);
        throw error;
    }
};

export const deleteHotSlide = async (hot_slide_id: string) => {
    try {
        const response = await api.delete(`/hot-slides/${hot_slide_id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting chapter slide:', error);
        throw error;
    }
};


export const addSlideToTop = async (data: { slide_id: string; parent_id: string }) => {
    const response = await api.post('/top-slides/', data);
    return response.data;
};
export const duplicateTopSlide = async (project_id: string, slide_id: string) => {
    const response = await api.post(`/top-slides/apply-to-all/${project_id}/${slide_id}`, {});
    return response.data;
};
export const getTopSlides = async (parent_id: string) => {
    const response = await api.get(`/top-slides/parent/${parent_id}`, { params: {  } });
    return response.data;
};
export const reorderTopSlides = async (parent_id: string, newOrder: string[]) => {
    try {
        const response = await api.put(`/top-slides/${parent_id}/reorder`, newOrder);
        return response.data;
    } catch (error) {
        console.error('Error reordering slides:', error);
        throw error;
    }
};

export const deleteTopSlide = async (top_slide_id: string) => {
    try {
        const response = await api.delete(`/top-slides/${top_slide_id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting chapter slide:', error);
        throw error;
    }
};
export const editVisitName = async (
    visitId: string,
    visitName: string,
    visitIsDefault: boolean,
    visitMCDNonprescriber:string,
    visitMCDTrialist:string,
    visitMCDUser:string,
    visitMCDAdvocate:string,
    visitEndoNonprescriber:string,
    visitEndoTrialist:string,
    visitEndoUser:string,
    visitEndoAdvocate:string,
    visitObstetricsNonprescriber:string,
    visitObstetricsTrialist:string,
    visitObstetricsUser:string,
    visitObstetricsAdvocate:string,
    visitPeriNonprescriber:string,
    visitPeriTrialist:string,
    visitPeriUser:string,
    visitPeriAdvocate:string,
    visitPostNonprescriber:string,
    visitPostTrialist:string,
    visitPostPrescriber:string,
    visitPostAdvocate:string
) => {
    try {
        const response = await api.patch(`/visits/${visitId}/rename?name=${visitName}&is_default=${visitIsDefault}&mcd_nonprescriber=${visitMCDNonprescriber}&mcd_trialist=${visitMCDTrialist}&mcd_user=${visitMCDUser}&mcd_advocate=${visitMCDAdvocate}&endo_nonprescriber=${visitEndoNonprescriber}&endo_trialist=${visitEndoTrialist}&endo_user=${visitEndoUser}&endo_advocate=${visitEndoAdvocate}&obstetrics_nonprescriber=${visitObstetricsNonprescriber}&obstetrics_trialist=${visitObstetricsTrialist}&obstetrics_user=${visitObstetricsUser}&obstetrics_advocate=${visitObstetricsAdvocate}&peri_nonprescriber=${visitPeriNonprescriber}&peri_trialist=${visitPeriTrialist}&peri_user=${visitPeriUser}&peri_advocate=${visitPeriAdvocate}&post_nonprescriber=${visitPostNonprescriber}&post_trialist=${visitPostTrialist}&post_prescriber=${visitPostPrescriber}&post_advocate=${visitPostAdvocate}`, { });
        return response.data;
    } catch (error) {
        console.error('Error reordering slides:', error);
        throw error;
    }
};
export const generateVisit = async (visitId: string) => {
    const response = await api.post(`/visits/${visitId}/generate_visit`, {});
    return response.data;
};


export const addMultiDirection = async (data: { link_id: string; scenario_id: string; from_visit_id: string; to_visit_id: string }) => {
    const response = await api.post('/multi-direction/', data);
    return response.data;
};
export const deleteMultiDirection = async (multiDirectionId: string) => {
    try {
        const response = await api.delete(`/multi-direction/${multiDirectionId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting multi-direction:', error);
        throw error;
    }
};
export const updateMultiDirection = async (multiDirectionId: string, data: {cenario_id: string; from_visit_id: string; to_visit_id: string}) => {
    const response = await api.put(`/multi-direction/${multiDirectionId}`, data);
    return response.data;
};
export const getMultiDirections = async (link_id: string) => {
    const response = await api.get(`/multi-direction/${link_id}`, { params: {  } });
    return response.data;
};


export default api;
